import React from 'react';
import { Plus, X } from 'lucide-react';

const Step6Additional = ({ formData, onChange }) => {
    const otherMediaLinks = formData.otherMediaLinks || [];

    const handleMediaLinkChange = (index, value) => {
        const updated = [...otherMediaLinks];
        updated[index] = value;
        onChange('otherMediaLinks', updated);
    };

    const addMediaLink = () => {
        onChange('otherMediaLinks', [...otherMediaLinks, '']);
    };

    const removeMediaLink = (index) => {
        onChange('otherMediaLinks', otherMediaLinks.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-soil-900 mb-4">Additional Information</h3>
                <p className="text-sm text-soil-600 mb-6">
                    Optional details to enhance your property listing
                </p>
            </div>

            {/* Video Tour URL */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Video Tour URL
                </label>
                <input
                    type="url"
                    value={formData.videoTourUrl || ''}
                    onChange={(e) => onChange('videoTourUrl', e.target.value)}
                    className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                    placeholder="https://youtube.com/watch?v=..."
                />
                <p className="mt-1 text-xs text-soil-400">
                    YouTube, Vimeo, or other video platform URL
                </p>
            </div>

            {/* Virtual Tour URL */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Virtual Tour URL
                </label>
                <input
                    type="url"
                    value={formData.virtualTourUrl || ''}
                    onChange={(e) => onChange('virtualTourUrl', e.target.value)}
                    className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                    placeholder="https://matterport.com/..."
                />
                <p className="mt-1 text-xs text-soil-400">
                    360° virtual tour or 3D walkthrough URL
                </p>
            </div>

            {/* Featured Image URL (if not uploaded) */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Featured Image URL
                </label>
                <input
                    type="url"
                    value={formData.featuredImageUrl || ''}
                    onChange={(e) => onChange('featuredImageUrl', e.target.value)}
                    className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                    placeholder="https://example.com/image.jpg"
                />
                <p className="mt-1 text-xs text-soil-400">
                    Optional: Link to an external featured image (if not using uploaded images)
                </p>
            </div>

            {/* Other Media Links */}
            <div>
                <label className="block text-sm font-medium text-soil-600 mb-2">
                    Other Media Links
                </label>
                <p className="text-xs text-soil-400 mb-3">
                    Brochures, floor plans, or any other supporting links
                </p>
                <div className="space-y-2">
                    {otherMediaLinks.map((link, index) => (
                        <div key={index} className="flex items-center space-x-2">
                            <input
                                type="url"
                                value={link}
                                onChange={(e) => handleMediaLinkChange(index, e.target.value)}
                                className="flex-1 px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                                placeholder="https://example.com/floor-plan.pdf"
                            />
                            <button
                                type="button"
                                onClick={() => removeMediaLink(index)}
                                className="p-2 text-soil-400 hover:text-murram-500 transition-colors"
                                aria-label="Remove link"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={addMediaLink}
                    className="mt-2 flex items-center text-sm font-medium text-highland-500 hover:text-highland-600"
                >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Link
                </button>
            </div>

            {/* Contact Preferences */}
            <div>
                <h4 className="text-md font-medium text-soil-900 mb-4">Contact Preferences</h4>
                <p className="text-sm text-soil-600 mb-4">
                    How would you like potential tenants to contact you?
                </p>

                <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="allowContactViaEmail"
                            checked={formData.allowContactViaEmail !== false}
                            onChange={(e) => onChange('allowContactViaEmail', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="allowContactViaEmail" className="text-sm font-medium text-soil-600 cursor-pointer flex-1">
                            Allow contact via email
                        </label>
                    </div>

                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="allowContactViaPhone"
                            checked={formData.allowContactViaPhone !== false}
                            onChange={(e) => onChange('allowContactViaPhone', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="allowContactViaPhone" className="text-sm font-medium text-soil-600 cursor-pointer flex-1">
                            Allow contact via phone
                        </label>
                    </div>

                    <div className="flex items-center space-x-3 p-4 border border-soil-100 rounded-lg">
                        <input
                            type="checkbox"
                            id="allowViewingRequests"
                            checked={formData.allowViewingRequests !== false}
                            onChange={(e) => onChange('allowViewingRequests', e.target.checked)}
                            className="h-5 w-5 text-highland-500 focus:ring-highland-500 border-soil-100 rounded"
                        />
                        <label htmlFor="allowViewingRequests" className="text-sm font-medium text-soil-600 cursor-pointer flex-1">
                            Allow viewing requests
                        </label>
                    </div>
                </div>
            </div>

            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4">
                <p className="text-sm text-highland-600">
                    <strong>Tip:</strong> Adding a video tour or virtual walkthrough can increase engagement by up to 80%!
                </p>
            </div>
        </div>
    );
};

export default Step6Additional;
